import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, Loader2, PackageOpen, X } from "lucide-react";
import { apiFetch } from "../utils/apiClient";
import { getUserFacingError } from "../utils/userFacingError";

const money = (value) => `${new Intl.NumberFormat("fr-FR", {
  maximumFractionDigits: 0,
}).format(Number(value) || 0)} FCFA`;

const quantity = (value) => new Intl.NumberFormat("fr-FR", {
  maximumFractionDigits: 3,
}).format(Number(value) || 0);

const statusLabel = (status) => ({
  closed: "Clôturée",
  ready: "À clôturer",
  provisional: "Provisoire",
  in_progress: "En cours",
}[status] || "En cours");

const netMovement = (row = {}) => Number(row.entries || 0)
  + Number(row.production || 0)
  + Number(row.adjustments || 0)
  - Number(row.outputs || 0)
  - Number(row.losses || 0)
  - Number(row.transfers || 0);

const StockSituationPanel = ({
  initialData,
  date = null,
  isDarkMode = true,
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(initialData || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setData(initialData || null);
  }, [initialData]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [open]);

  const loadDetails = async () => {
    setOpen(true);
    if (Array.isArray(data?.rows)) return;
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (date) params.set("date", date);
      const query = params.toString();
      const response = await apiFetch(`/api/waise/v2/daily-situation${query ? `?${query}` : ""}`, {
        cache: "no-store",
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || "Situation indisponible.");
      setData(payload);
    } catch (loadError) {
      setError(getUserFacingError(loadError, "Situation indisponible. Réessayez."));
    } finally {
      setLoading(false);
    }
  };

  const rows = useMemo(() => Array.isArray(data?.rows) ? data.rows : [], [data?.rows]);
  if (!initialData?.enabled) return null;
  const summary = data?.summary || initialData?.summary || {};
  const complete = summary.valuation_complete === true;
  const openingValue = complete ? summary.opening_value : null;
  const closingValue = complete ? summary.closing_value : null;
  const shell = isDarkMode
    ? "border-white/10 bg-[#0c162b] text-white"
    : "border-slate-200 bg-white text-slate-900";

  return <>
    <button
      type="button"
      onClick={loadDetails}
      className={`${shell} ${className} w-full rounded-3xl border p-4 text-left shadow-lg transition hover:border-amber-300/40 sm:p-6`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Waise</p>
          <h2 className="mt-1 text-lg font-semibold">Situation du stock</h2>
          <p className="mt-1 text-sm opacity-60">
            {statusLabel(initialData.status)} · {Number(summary.item_count || 0)} article{Number(summary.item_count || 0) > 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <div className="hidden text-right sm:block">
            <p className="font-semibold">{closingValue == null ? "Valeur incomplète" : money(closingValue)}</p>
            <p className="text-xs opacity-55">Stock final</p>
          </div>
          <ArrowRight className="h-5 w-5 text-amber-300" />
        </div>
      </div>
    </button>

    {open ? <div
      role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget) setOpen(false); }}
      className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-950/75 p-0 backdrop-blur-sm sm:items-center sm:p-4"
    >
      <section role="dialog" aria-modal="true" aria-labelledby="stock-situation-title" className="flex max-h-[92vh] w-full max-w-4xl flex-col rounded-t-3xl border border-white/10 bg-[#091226] text-white shadow-2xl sm:rounded-3xl">
        <header className="flex items-start justify-between gap-3 border-b border-white/10 p-4 sm:p-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Waise</p>
            <h2 id="stock-situation-title" className="mt-1 text-xl font-bold">Situation du stock</h2>
            <p className="mt-1 text-sm text-white/60">{data?.period?.label || data?.business_date || date || "Journée en cours"}</p>
          </div>
          <button type="button" aria-label="Fermer" onClick={() => setOpen(false)} className="rounded-xl border border-white/10 p-2 text-white/70 hover:bg-white/10"><X className="h-5 w-5" /></button>
        </header>

        <div className="min-h-0 overflow-y-auto p-3 sm:p-6">
          {loading ? <div className="flex min-h-48 items-center justify-center gap-3 text-white/70"><Loader2 className="h-5 w-5 animate-spin" />Chargement…</div> : null}
          {!loading && error ? <div className="rounded-2xl border border-rose-400/25 bg-rose-500/10 p-4 text-sm text-rose-100">{error}</div> : null}
          {!loading && !error ? <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-2xl bg-white/5 p-3"><small className="text-white/55">Stock initial</small><strong className="mt-1 block">{openingValue == null ? "Non valorisé" : money(openingValue)}</strong></div>
              <div className="rounded-2xl bg-white/5 p-3"><small className="text-white/55">Stock final</small><strong className="mt-1 block">{closingValue == null ? "Non valorisé" : money(closingValue)}</strong></div>
              <div className="rounded-2xl bg-white/5 p-3"><small className="text-white/55">Articles</small><strong className="mt-1 block">{Number(summary.item_count || rows.length)}</strong></div>
              <div className="rounded-2xl bg-white/5 p-3"><small className="text-white/55">Sans prix</small><strong className="mt-1 block">{Number(summary.missing_cost_item_count || 0)}</strong></div>
            </div>
            <div className="mt-4 space-y-2">
              {rows.map((row) => <article key={`${row.stock_item_id}-${row.warehouse_id}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0"><p className="font-semibold">{row.item_name}</p><p className="text-xs text-white/50">{row.warehouse_name}</p></div>
                  <p className="shrink-0 font-semibold">{row.closing_value == null ? "Non valorisé" : money(row.closing_value)}</p>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                  <div><small className="block text-white/45">Initial</small>{quantity(row.opening_quantity)} {row.unit}</div>
                  <div><small className="block text-white/45">Mouvements</small>{netMovement(row) > 0 ? "+" : ""}{quantity(netMovement(row))} {row.unit}</div>
                  <div><small className="block text-white/45">Final</small>{quantity(row.closing_quantity)} {row.unit}</div>
                </div>
              </article>)}
              {!rows.length ? <div className="py-10 text-center text-sm text-white/50"><PackageOpen className="mx-auto mb-3 h-8 w-8" />Aucun stock à afficher.</div> : null}
            </div>
          </> : null}
        </div>
      </section>
    </div> : null}
  </>;
};

export default StockSituationPanel;
